import React, { useEffect, useState, Suspense, lazy } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UserList } from "components/Conversations";
import {
  connectChatsSocketStart,
  refetchConversationsListStart,
  setSelectedConversation,
} from "redux/chats/chats.actions";
import "./styles.scss";
import useMediaQuery from "use-mediaquery";
import { useNavigate, Navigate } from "react-router-dom";
import { Routes, Route } from "react-router-dom";
import routes from "routes";
import PackageExpireModal from "components/Modals/PackageExpireModal";
import UpdatedFeaturesModal from "components/Modals/UpdatedFeaturesModal";
import ConversationPageLoader from "./components/PageLoader";
import TransferedChats from "./components/TransferedChats";
import PageLazyLoadFallback from "./components/PageLazyLoadFallback";
import SettingsMenu from "components/Conversations/Settings/SettingsMenu";
import { setIsLoggedInUser } from "redux/auth/auth.actions";
import MainHeader from "components/MainHeader";
import Alerts from "components/Alerts";
import { useLocation } from "react-router-dom";
import PopupMessage from "components/PopupMessage";
import { setShowPopupNotifDisplay } from "redux/general-data/general-data.actions";
import { POPUP_NAMES } from "utils/constants";
import { clsx } from "clsx";
import useChangeDocumentTitle from "hooks/useChangeDocumentTitle";

const ChatPage = lazy(() => import("./components/Chat"));
const HomePage = lazy(() => import("./components/UserChannels"));
const OnlineUsers = lazy(() => import("./components/OnlineUsers"));
const Partners = lazy(() => import("./components/Partners"));

const getSelectedConversation = ({ chatsData }) =>
  chatsData.selectedConversation;
const getIsConnected = ({ connectionData }) => connectionData.isConnected;
const getShowNotifPopupStatus = ({ generalData }) => generalData.showNotifPopup;

const ConversationsPage = () => {
  useChangeDocumentTitle();

  const selectedConversation = useSelector(getSelectedConversation);
  const isConnected = useSelector(getIsConnected);
  const showNotifPopup = useSelector(getShowNotifPopupStatus);

  const [loading, setLoading] = useState(true);
  const [convBodyContent, setConvBodyContent] = useState("userChannels");
  const [showSettings, setShowSettings] = useState(false);
  const [showUserInfo, setShowUserInfo] = useState(false);
  const isMobileDevice = useMediaQuery("(max-width: 650px)");

  const location = useLocation();

  // Alerts component shuld not get rendered at top in chats page. because in that page exceptionally we render it somewhere else
  const shouldRenderAlertsComponent =
    !location.pathname.includes(routes.chats) || isMobileDevice;

  const toggleUserInfo = () => {
    setShowUserInfo(!showUserInfo);
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isSmallDevice = useMediaQuery("(max-width: 800px)");

  const setDefaultConvBody = () => {
    if (isSmallDevice) {
      setConvBodyContent("messages");
    } else {
      setConvBodyContent("userChannels");
    }
  };

  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };

  const notifpopupContent = (
    <p>
      <p>
        <b>نکته: </b>
        جهت دریافت مداوم اعلانات در نسخه اپلیکیشن موبایل می بایست برنامه را در
        پس زمینه (background) باز نگه دارید. با بسته شدن برنامه ممکن است بخشی از
        اعلانات را دریافت نکنید.
      </p>
    </p>
  );

  const onCloseNotifPopup = () => {
    dispatch(setShowPopupNotifDisplay(false));
  };

  const renderNotifPopupMsg = (
    <PopupMessage
      onClose={onCloseNotifPopup}
      popupName={POPUP_NAMES.NOTIF_POPUP}
      content={notifpopupContent}
    />
  );

  useEffect(() => {
    dispatch(connectChatsSocketStart());
    setDefaultConvBody();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedConversation) {
      setDefaultConvBody();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation]);

  useEffect(() => {
    if (!convBodyContent) {
      setDefaultConvBody();
    }
    if (convBodyContent !== "messages") {
      dispatch(setSelectedConversation(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convBodyContent]);

  useEffect(() => {
    if (isConnected) {
      setLoading(false);
    }
  }, [isConnected]);

  useEffect(() => {
    const id = localStorage.getItem("raychat_web_app_agentId");
    const token = localStorage.getItem("raychat_web_app_token");

    if (!id || !token) {
      dispatch(setIsLoggedInUser(false));
      navigate("/login");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        // just refetch two main lists
        dispatch(refetchConversationsListStart());
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderConversationsList = (
    <div className={clsx("conv__list", isSmallDevice && "w-mobile-100")}>
      <UserList
        toggleSettings={toggleSettings}
        setConvBodyContent={setConvBodyContent}
      />
    </div>
  );

  if (loading) return <ConversationPageLoader />;

  return (
    <>
      <PackageExpireModal />
      <UpdatedFeaturesModal />
      {showNotifPopup && renderNotifPopupMsg}
      <section className="conv flex">
        <SettingsMenu
          setShowSettings={setShowSettings}
          showSettings={showSettings}
        />
        {/* conversatinos list (Desktop view) */}
        {!isSmallDevice && renderConversationsList}

        <div className="main-section-wrapper flex">
          <MainHeader
            toggleSettings={toggleSettings}
            toggleUserInfo={toggleUserInfo}
          />
          {shouldRenderAlertsComponent && <Alerts />}

          <Suspense fallback={<PageLazyLoadFallback />}>
            <Routes>
              <Route
                path={routes.convsList}
                element={
                  isSmallDevice ? (
                    renderConversationsList
                  ) : (
                    <Navigate to={routes.home} />
                  )
                }
              />

              <Route
                path={`${routes.chats}/:convId`}
                element={
                  <ChatPage
                    toggleUserInfo={toggleUserInfo}
                    showUserInfo={showUserInfo}
                  />
                }
              />

              <Route
                path={routes.home}
                element={
                  <div className="conv__body">
                    <HomePage setConvBodyContent={setConvBodyContent} />
                  </div>
                }
              />
              <Route
                path={routes.users}
                element={
                  <div className="conv__body">
                    <OnlineUsers setConvBodyContent={setConvBodyContent} />
                  </div>
                }
              />
              <Route
                path={routes.agents}
                element={
                  <div className="conv__body">
                    <Partners setConvBodyContent={setConvBodyContent} />
                  </div>
                }
              />

              <Route path="*" element={<Navigate to={routes.home} />} />
            </Routes>
          </Suspense>
        </div>

        <TransferedChats />
      </section>
    </>
  );
};

export default ConversationsPage;
